import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useLotes,
  useEstadisticasLotes,
  useActualizarVencimientoLote,
} from '../hooks/useLotes';
import { lotesAPI } from '../services/lotesAPI';
import { useToast } from '../hooks/useToast';
import PageHeader from '../components/PageHeader';
import { Package, Filter, AlertTriangle, Clock, Eye, Edit } from 'lucide-react';
import type { LoteIngredienteBackend } from '../types/lotes';

// Convierte "ACEITE MARAVILLA" o "aceite maravilla" en "Aceite Maravilla".
// Preserva minúsculas para artículos comunes para que no quede "De", "La", etc.
const toTitleCase = (s: string): string => {
  const lower = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'y', 'o', 'con', 'en']);
  return s
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) =>
      i > 0 && lower.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
};

const Lotes: React.FC = () => {
  const { success, error: showError } = useToast();
  const actualizarVencimiento = useActualizarVencimientoLote();
  const [filtros, setFiltros] = useState({
    // Usa nombre normalizado en lugar del id directo para agrupar
    // variantes de escritura del mismo ingrediente (ej: "Arroz" = "ARROZ" = "arroz").
    ingredienteNombre: '',
    estado: '',
    proximoVencer: false,
  });
  const [showFiltros, setShowFiltros] = useState(false);
  const [loteSeleccionado, setLoteSeleccionado] =
    useState<LoteIngredienteBackend | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [fechaVencimientoEdit, setFechaVencimientoEdit] = useState('');

  // Funciones para manejar modales
  const abrirDetalleModal = (lote: LoteIngredienteBackend) => {
    setLoteSeleccionado(lote);
    setShowDetalleModal(true);
  };

  const cerrarDetalleModal = () => {
    setShowDetalleModal(false);
    setLoteSeleccionado(null);
  };

  const abrirEditModal = (lote: LoteIngredienteBackend) => {
    setLoteSeleccionado(lote);
    setFechaVencimientoEdit(
      lote.fecha_vencimiento
        ? new Date(lote.fecha_vencimiento).toISOString().split('T')[0]
        : ''
    );
    setShowEditModal(true);
  };

  const cerrarEditModal = () => {
    setShowEditModal(false);
    setLoteSeleccionado(null);
    setFechaVencimientoEdit('');
  };

  const guardarEdicion = async () => {
    if (!loteSeleccionado) return;

    try {
      // Vacío = el lote no vence: se manda null para borrar la fecha.
      await actualizarVencimiento.mutateAsync({
        id: Number(loteSeleccionado.id),
        fechaVencimiento: fechaVencimientoEdit || null,
      });
      success(
        `Fecha de vencimiento actualizada para el lote ${loteSeleccionado.numero_lote}`
      );
      cerrarEditModal();
    } catch (error: any) {
      console.error('Error al actualizar lote:', error);
      showError(
        error?.response?.data?.message ||
          error?.message ||
          'No se pudo actualizar la fecha de vencimiento'
      );
    }
  };

  // Consultas React Query
  const {
    data: lotesData,
    isLoading: loadingLotes,
    error: errorLotes,
  } = useLotes({
    ingredienteNombre: filtros.ingredienteNombre || undefined,
    estado: filtros.estado || undefined,
    proximoVencer: filtros.proximoVencer || undefined,
  });

  // Lista filtrable: sólo ingredientes que tienen lotes activos, deduplicados
  // por nombre normalizado en el backend. Evita mostrar entradas que al
  // seleccionarlas devolverían 0 resultados.
  const { data: ingredientesFiltro = [], isLoading: loadingIngredientes } =
    useQuery({
      queryKey: ['lotes-ingredientes-filtro'],
      queryFn: () => lotesAPI.obtenerIngredientesConLotes(),
      staleTime: 5 * 60 * 1000,
    });
  const { data: estadisticasData } = useEstadisticasLotes();

  const lotes = lotesData?.lotes || [];
  const loading = loadingLotes || loadingIngredientes;

  const ingredientesUnicos = (ingredientesFiltro as any[]).map((ing) => ({
    key: ing.nombre_key,
    nombreCanonico: toTitleCase(ing.nombre_sample),
    lotesCount: ing.lotes_count,
    variantesCount: ing.variantes_count,
  }));

  // Función para determinar el estado del lote
  const obtenerEstadoLote = (lote: LoteIngredienteBackend) => {
    const hoy = new Date();
    const fechaVencimiento = lote.fecha_vencimiento
      ? new Date(lote.fecha_vencimiento)
      : null;

    if (parseFloat(lote.cantidad_disponible) <= 0) return 'agotado';
    if (!fechaVencimiento) return 'disponible';

    const diasRestantes = Math.ceil(
      (fechaVencimiento.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diasRestantes < 0) return 'vencido';
    if (diasRestantes <= 5) return 'proximo-vencer';
    return 'disponible';
  };

  // Función para obtener el color del estado
  const obtenerColorEstado = (estado: string) => {
    switch (estado) {
      case 'disponible':
        return 'text-green-600 bg-green-100';
      case 'proximo-vencer':
        return 'text-yellow-600 bg-yellow-100';
      case 'vencido':
        return 'text-red-600 bg-red-100';
      case 'agotado':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Función para formatear fecha
  const formatearFecha = (fecha: Date | string) => {
    return new Date(fecha).toLocaleDateString('es-CL');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Cargando lotes...</span>
      </div>
    );
  }

  if (errorLotes) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error al cargar lotes
            </h3>
            <div className="mt-2 text-sm text-red-700">
              No se pudieron cargar los lotes. Por favor, intenta nuevamente.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: "url('/bg_dash.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/85 dark:bg-gray-900/85"></div>

      <PageHeader
        title="Gestión de Lotes"
        helpSeccion="lotes"
        subtitle="Administra los lotes de ingredientes y su trazabilidad"
      />

      {/* Contenido */}
      <div className="relative z-10 p-6">
        <div className="mb-8">
          {/* Estadísticas */}
          {estadisticasData && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
                    <Package className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Total Lotes
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {estadisticasData.total || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
                    <div className="h-6 w-6 bg-green-600 dark:bg-green-400 rounded-full"></div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Disponibles
                    </p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {estadisticasData.disponibles || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Próximos a Vencer
                    </p>
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                      {estadisticasData.proximosVencer || 0}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
                <div className="flex items-center">
                  <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Vencidos
                    </p>
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                      {estadisticasData.vencidos || 0}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filtros */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 mb-6">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Filtros de Búsqueda
                </h3>
                <button
                  onClick={() => setShowFiltros(!showFiltros)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {showFiltros ? 'Ocultar' : 'Mostrar'} Filtros
                </button>
              </div>
            </div>

            {showFiltros && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ingrediente
                    </label>
                    <select
                      value={filtros.ingredienteNombre}
                      onChange={(e) =>
                        setFiltros((prev) => ({
                          ...prev,
                          ingredienteNombre: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Todos los ingredientes</option>
                      {ingredientesUnicos.map((ing) => (
                        <option key={ing.key} value={ing.nombreCanonico}>
                          {ing.nombreCanonico}
                          {' · '}
                          {ing.lotesCount} lote{ing.lotesCount === 1 ? '' : 's'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estado
                    </label>
                    <select
                      value={filtros.estado}
                      onChange={(e) =>
                        setFiltros((prev) => ({
                          ...prev,
                          estado: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Todos los estados</option>
                      <option value="disponible">Disponible</option>
                      <option value="proximo-vencer">Próximo a vencer</option>
                      <option value="vencido">Vencido</option>
                      <option value="agotado">Agotado</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filtros.proximoVencer}
                        onChange={(e) =>
                          setFiltros((prev) => ({
                            ...prev,
                            proximoVencer: e.target.checked,
                          }))
                        }
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Solo próximos a vencer
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tabla de lotes */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Lista de Lotes
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Lote
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ingrediente
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Cantidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Disponible
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Vencimiento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {lotes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <Package className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                          No hay lotes
                        </h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Los lotes se crean automáticamente al recepcionar
                          órdenes de compra.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    lotes.map((lote: any) => {
                      const estado = obtenerEstadoLote(lote);
                      return (
                        <tr
                          key={lote.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {lote.numero_lote}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              Ingreso: {formatearFecha(lote.fecha_ingreso)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {lote.ingrediente.nombre}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {lote.ingrediente.categoria}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {lote.cantidad} {lote.ingrediente.unidad_medida}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {lote.cantidad_disponible}{' '}
                            {lote.ingrediente.unidad_medida}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {lote.fecha_vencimiento
                              ? formatearFecha(lote.fecha_vencimiento)
                              : 'Sin vencimiento'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${obtenerColorEstado(estado)}`}
                            >
                              {estado === 'disponible' && 'Disponible'}
                              {estado === 'proximo-vencer' &&
                                'Próximo a vencer'}
                              {estado === 'vencido' && 'Vencido'}
                              {estado === 'agotado' && 'Agotado'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {lote.proveedor.nombre}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => abrirDetalleModal(lote)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 p-1 rounded transition-colors"
                                title="Ver detalle"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => abrirEditModal(lote)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 p-1 rounded transition-colors"
                                title="Editar fecha de vencimiento"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Detalle */}
      {showDetalleModal && loteSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Detalle del Lote
                </h3>
                <button
                  onClick={cerrarDetalleModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Número de Lote
                  </label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.numero_lote}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ingrediente
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.ingrediente.nombre}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cantidad Total
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.cantidad}{' '}
                    {loteSeleccionado.ingrediente.unidad_medida}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cantidad Disponible
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.cantidad_disponible}{' '}
                    {loteSeleccionado.ingrediente.unidad_medida}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Ingreso
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {formatearFecha(loteSeleccionado.fecha_ingreso)}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Vencimiento
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.fecha_vencimiento
                      ? formatearFecha(loteSeleccionado.fecha_vencimiento)
                      : 'Sin vencimiento'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Proveedor
                  </label>
                  <p className="text-lg text-gray-900 dark:text-gray-100">
                    {loteSeleccionado.proveedor.nombre}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado
                  </label>
                  <span
                    className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${obtenerColorEstado(obtenerEstadoLote(loteSeleccionado))}`}
                  >
                    {(() => {
                      const estado = obtenerEstadoLote(loteSeleccionado);
                      switch (estado) {
                        case 'disponible':
                          return 'Disponible';
                        case 'proximo-vencer':
                          return 'Próximo a vencer';
                        case 'vencido':
                          return 'Vencido';
                        case 'agotado':
                          return 'Agotado';
                        default:
                          return 'Desconocido';
                      }
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={cerrarDetalleModal}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edición */}
      {showEditModal && loteSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Editar Fecha de Vencimiento
                </h3>
                <button
                  onClick={cerrarEditModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Lote: {loteSeleccionado.numero_lote}
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {loteSeleccionado.ingrediente.nombre}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nueva Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  value={fechaVencimientoEdit}
                  onChange={(e) => setFechaVencimientoEdit(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Deja vacío si no tiene fecha de vencimiento
                </p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={cerrarEditModal}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={guardarEdicion}
                disabled={actualizarVencimiento.isPending}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actualizarVencimiento.isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Lotes;
